import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { AlertCircle, ArrowRight, Loader2, Lock, ShieldCheck } from "lucide-react";
import { Redirect } from "wouter";
import { toast } from "@/hooks/use-toast";

const loginSchema = z.object({
  username: z.string().min(1, "Username is required"),
  password: z.string().min(1, "Password is required"),
});

type LoginFormData = z.infer<typeof loginSchema>;

function LoginForm() {
  const { login } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const BRAND_BLUE = "rgb(30 58 138)";

  const onSubmit = async (data: LoginFormData) => {
    try {
      setIsLoading(true);
      setError(null);
      await login(data.username, data.password);
    } catch (err: any) {
      const message = err.response?.data?.message || "Invalid credentials";
      setError(message);
      toast({ title: "Login Failed", description: message, variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full flex items-center justify-center bg-[#f8fafc] overflow-hidden">
      {/* Dynamic Background Mesh */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-[10%] -left-[5%] w-[60%] h-[60%] rounded-full bg-blue-800/10 blur-[120px]" />
        <div className="absolute top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-indigo-700/5 blur-[100px]" />
      </div>

      <div className="relative z-10 w-full max-w-[480px] px-6 py-10">
        {/* Full-width Logo Header */}
        <div className="flex flex-col items-center text-center mb-8">
          <div className="mb-6 relative">
            <img
              src="/logo.png"
              alt="Someaknea Tech"
              className="h-16 w-auto object-contain transition-transform hover:scale-105 duration-300"
            />
            {/* Beta Badge on Logo */}
            <span className="absolute -top-1 -right-8 bg-blue-100 text-blue-800 text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-tighter border border-blue-200">
              Beta
            </span>
          </div>
          <h3 className="text-[16px] text-slate-500 max-w-[320px] leading-relaxed">
            Use your account to access the dashboard and manage everything.
          </h3>
        </div>

        <div className="bg-white rounded-3xl shadow-[0_20px_50px_rgba(30,58,138,0.12)] border border-slate-100 p-8 md:p-12">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-[24px] font-bold tracking-tight text-slate-900">Sign in to Your Account</h2>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-100 text-red-700 text-sm p-4 rounded-xl flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-red-500" />
                <span className="font-medium">{error}</span>
              </div>
            )}

            <div className="space-y-5">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700 ml-1" htmlFor="username">
                  Username or Email
                </label>
                <Input
                  {...register("username")}
                  id="username"
                  placeholder="admin@system.com"
                  className="h-12 px-4 bg-slate-50/50 border-slate-200 focus:border-blue-800 focus:ring-4 focus:ring-blue-800/5 transition-all "
                  disabled={isLoading}
                />
                {errors.username && <p className="text-xs text-red-500 mt-1 ml-1">{errors.username.message}</p>}
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center px-1">
                  <label className="text-sm font-semibold text-slate-700" htmlFor="password">
                    Password
                  </label>
                  <a href="#" className="text-xs font-bold text-blue-800 hover:underline">
                    Forgot password?
                  </a>
                </div>
                <Input
                  {...register("password")}
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  className="h-12 px-4 bg-slate-50/50 border-slate-200 focus:border-blue-800 focus:ring-4 focus:ring-blue-800/5 transition-all "
                  disabled={isLoading}
                />
                {errors.password && <p className="text-xs text-red-500 mt-1 ml-1">{errors.password.message}</p>}
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full h-12 text-white font-bold  shadow-lg shadow-blue-900/20 hover:opacity-90 active:scale-[0.98] transition-all flex items-center justify-center gap-2 group bg-blue-600 text-white font-medium hover:bg-blue-700 shadow-sm"
            >
              {isLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  <span>Sign In</span>
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </Button>
          </form>

          {/* Security Footer inside card */}
          <div className="mt-8 pt-6 border-t border-slate-100">
            <div className="flex items-center justify-center gap-2 text-[#94a3b8] text-[11px] font-bold uppercase tracking-[0.15em] mb-4">
              <ShieldCheck className="h-4 w-4 text-blue-800/40" />
              Secure, Fast and Reliable
            </div>

            {/* Contact Team Section */}
            <div className="bg-slate-50/80 rounded-2xl p-4 text-center">
              <p className="text-sm text-slate-600">
                Don't have account?{" "}
                <a
                  href="https://t.me/+G_fJ4M26JIIxNDYy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 font-bold text-blue-800 hover:text-blue-700 transition-colors underline-offset-4 hover:underline"
                >
                  <svg className="h-3.5 w-3.5 fill-[#229ED9]" role="img" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.303.48-.429-.01-.1.255-1.256-.634-1.415-.544-2.536-.833-2.536-.833-.501-.158-.501-.501.108-.745 2.16-.881 4.544-1.848 7.152-2.902.137-.055.27-.082.4-.082z" />
                  </svg>
                  Contact our team
                </a>
              </p>
            </div>
          </div>
        </div>

        {/* External Footer with Version */}
        <div className="mt-10 text-center space-y-2">
          <p className="text-slate-400 text-sm">© 2025 Someaknea Tech. All rights reserved.</p>
          <p className="text-[10px] font-bold text-blue-800/40 uppercase tracking-[0.2em]">Build v0.0.1 Beta</p>
        </div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex flex-col items-center justify-center gap-4">
        <Loader2 className="h-10 w-10 text-blue-800 animate-spin" />
        <span className="text-xs font-bold text-blue-900/40 uppercase tracking-widest">v0.0.1 Beta • Initializing</span>
      </div>
    );
  }

  if (isAuthenticated) return <Redirect to="/dashboard" replace />;

  return <LoginForm />;
}